<template>
  <LayoutContainer center :maxWidth="600">
    <div class="nps-header">
      <img :src="LogoGAE" alt="GAE Conseil" class="nps-logo" />
    </div>

    <div v-if="store.loading" class="nps-loading">Chargement...</div>

    <div v-else-if="store.error" class="nps-error">
      <p>{{ store.error }}</p>
    </div>

    <div v-else-if="store.submitted || store.formData?.alreadyAnswered" class="nps-success">
      <h2>Merci pour votre retour !</h2>
      <p>Votre réponse a bien été prise en compte. GAE Conseil vous remercie de votre collaboration.</p>
    </div>

    <template v-else-if="store.formData">
      <h2>{{ store.formData.title }}</h2>
      <p class="nps-meta">{{ store.formData.date }} — {{ store.formData.customer }}</p>

      <p class="nps-intro">
        Dans le cadre de l'amélioration continue de nos actions de prévention des addictions,
        nous vous invitons à répondre aux questions suivantes :
      </p>

      <form class="nps-form" @submit.prevent="submit">
        <template v-for="question in store.formData.questions" :key="question.id">
          <InputCursor
            v-if="question.type === 8"
            :id="`nps-q-${question.id}`"
            :label="question.label"
            :range="10"
            :value="5"
            :required="question.flags.includes('FLAG_REQUIRED')"
            :errors="v$[question.id]?.$errors"
            v-model="answers[question.id]"
          />
          <InputArea
            v-else-if="question.type === 3"
            :id="`nps-q-${question.id}`"
            :label="question.label"
            :required="question.flags.includes('FLAG_REQUIRED')"
            :errors="v$[question.id]?.$errors"
            v-model="answers[question.id]"
            placeholder="Votre réponse..."
          />
        </template>

        <CommonButton filled :loading="store.submitting" :disabled="v$.$invalid" @click="submit">
          Envoyer
        </CommonButton>
      </form>
    </template>
  </LayoutContainer>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import LogoGAE from '@/assets/logo-GAE-color.svg'
import LayoutContainer from '@/components/layout/LayoutContainer.vue'
import InputCursor from '@/components/common/input/InputCursor.vue'
import InputArea from '@/components/common/input/InputArea.vue'
import CommonButton from '@/components/common/CommonButton.vue'
import { useNpsStore } from '@/stores/gae/store_nps'

const route = useRoute()
const store = useNpsStore()

// Cursor questions (type 8) default to 5, text areas (type 3) to ''.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const answers = reactive<Record<number, any>>({})

const rules = computed(() => {
  const r: Record<number, object> = {}
  for (const question of store.formData?.questions ?? []) {
    if (question.flags.includes('FLAG_REQUIRED')) {
      r[question.id] = {
        required: helpers.withMessage('Le champ est requis.', required),
      }
    }
  }
  return r
})

const v$ = useVuelidate(rules, answers as any)

onMounted(async () => {
  const token = route.query.token as string
  if (!token) {
    store.error = 'Lien invalide.'
    return
  }
  await store.fetchForm(token)
  for (const question of store.formData?.questions ?? []) {
    answers[question.id] = question.type === 8 ? 5 : ''
  }
})

async function submit() {
  const valid = await v$.value.$validate()
  if (!valid) return

  const token = route.query.token as string
  const payload = (store.formData?.questions ?? []).map((question) => ({
    questionId: question.id,
    value: String(answers[question.id] ?? ''),
  }))
  await store.submitForm(token, payload)
}
</script>

<style scoped>
.nps-header {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
}

.nps-logo {
  height: 60px;
}

.nps-loading,
.nps-error {
  text-align: center;
  padding: 40px 0;
  color: rgba(var(--vcrgb-dark), 0.6);
}

.nps-error {
  color: var(--vc-danger);
}

.nps-success {
  text-align: center;
  padding: 40px 0;
}

.nps-success h2 {
  margin-bottom: 16px;
}

.nps-meta {
  color: rgba(var(--vcrgb-dark), 0.6);
  margin-bottom: 24px;
}

.nps-intro {
  margin-bottom: 32px;
  font-style: italic;
}

.nps-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
